import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';

/**
 * <embrace-auth-required> — notice-view voor kanalen die authenticatie
 * vereisen (huidige callers: `repair-request`).
 *
 * Toont een centrale melding met een primaire CTA "Inloggen" — als
 * `loginUrl` is gezet opent die een nieuw tab/window naar de host-portal
 * inlog. Wanneer geen URL bekend is, valt de primaire knop terug op de
 * secundaire actie (terug naar de channel-picker).
 *
 * Bewust géén eigen lock-mechanisme: de host-page is bron-van-waarheid
 * voor authenticatie. Deze view is informatief — de daadwerkelijke
 * inlog-flow gebeurt buiten de widget.
 */
@customElement('embrace-auth-required')
export class EmbraceAuthRequired extends LitElement {
  /**
   * Optionele URL naar de inlog-pagina van de host-portal. Wanneer
   * gezet, opent de "Inloggen"-knop deze URL in een nieuw tab. Anders
   * verbergen we de primaire knop en valt de bezoeker terug op "Terug".
   */
  @property({ type: String })
  loginUrl = '';

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .body {
      flex: 1;
      padding: 28px 22px;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 14px;
      background: var(--ec-background);
    }

    .lock {
      width: 88px;
      height: 88px;
      border-radius: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 8px;
      background: var(--ec-warning-subtle);
      color: var(--ec-warning);
      box-shadow: 0 6px 18px -10px rgba(171, 101, 15, 0.45);
    }

    h3 {
      margin: 4px 0 0;
      font: 700 20px/1.25 var(--ec-font-display);
      color: var(--ec-text);
    }

    p {
      margin: 0;
      font: 400 14px/1.55 var(--ec-font-family);
      color: var(--ec-text-muted);
      max-width: 36ch;
    }

    .actions {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      width: 100%;
      max-width: 280px;
      margin-top: 8px;
    }

    .btn {
      appearance: none;
      -webkit-appearance: none;
      border: 0;
      cursor: pointer;
      padding: 12px 20px;
      border-radius: var(--ec-radius-button);
      font: 700 14px/1 var(--ec-font-family);
      text-decoration: none;
      text-align: center;
      transition: background var(--ec-duration-fast) var(--ec-ease-smooth),
                  color var(--ec-duration-fast) var(--ec-ease-smooth);
    }
    .btn-primary {
      background: var(--ec-primary);
      color: var(--ec-on-primary);
    }
    .btn-primary:hover { background: var(--ec-primary-hover); }
    .btn-secondary {
      background: transparent;
      color: var(--ec-text-muted);
      border: 1px solid var(--ec-border);
    }
    .btn-secondary:hover {
      color: var(--ec-text);
      background: var(--ec-surface-sunken);
    }
  `;

  override render() {
    const copy = i18n.authRequired;
    return html`
      <div class="body">
        <div class="lock" role="img" aria-label="Vergrendeld">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="4" y="11" width="16" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            <circle cx="12" cy="15.5" r="1.2" fill="currentColor" stroke="none" />
          </svg>
        </div>
        <h3>${copy.title}</h3>
        <p>${copy.body}</p>
        <div class="actions">
          ${this.loginUrl
            ? html`
                <a class="btn btn-primary" href=${this.loginUrl} target="_blank" rel="noreferrer">
                  ${copy.btnPrimary}
                </a>
              `
            : nothing}
          <button class="btn btn-secondary" @click=${this.onBack}>
            ${copy.btnSecondary}
          </button>
        </div>
      </div>
    `;
  }

  private onBack() {
    this.dispatchEvent(
      new CustomEvent('back-to-picker', { bubbles: true, composed: true }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-auth-required': EmbraceAuthRequired;
  }
}
